<template>
  <VaDropdown
    v-model="isDropdownOpen"
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge overlap :visible="unreadCount > 0" color="danger">
          <template #text>{{ unreadCount > 99 ? '99+' : unreadCount }}</template>
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>

    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <div class="flex justify-between items-center px-4 py-2 border-b">
        <span class="font-bold text-sm text-gray-600">Notifikasi</span>
        <button
          v-if="unreadCount > 0"
          class="text-xs text-primary hover:underline cursor-pointer bg-transparent border-none"
          @click="handleMarkAllRead"
        >
          Tandai semua dibaca
        </button>
      </div>

      <section class="sm:max-h-[320px] overflow-auto">
        <VaList class="space-y-1 mb-2">
          <div v-if="isLoading && notifications.length === 0" class="p-4 text-center text-gray-400 text-sm">
            Memuat...
          </div>

          <div v-else-if="notifications.length === 0" class="p-8 text-center flex flex-col items-center text-gray-400">
            <VaIcon name="notifications_off" size="large" color="gray" class="mb-2" />
            <span class="text-sm">Tidak ada notifikasi</span>
          </div>

          <template v-for="(item, index) in formattedNotifications" v-else :key="item.id">
            <VaListItem
              class="text-base cursor-pointer hover:bg-gray-100 transition duration-150 p-3"
              :class="{ 'bg-blue-50': !item.is_read }"
              @click="handleItemClick(item)"
            >
              <VaListItemSection icon class="mx-0 p-0 min-w-[30px]">
                <VaIcon :name="item.icon" :color="item.is_read ? 'gray' : 'primary'" size="small" />
              </VaListItemSection>

              <VaListItemSection>
                <div class="flex justify-between items-start">
                  <span class="font-semibold text-sm text-gray-800" :class="{ 'font-bold': !item.is_read }">
                    {{ item.title }}
                  </span>
                  <span class="text-[10px] text-gray-500 ml-2 whitespace-nowrap">
                    {{ item.timeHuman }}
                  </span>
                </div>
                <div class="text-xs text-gray-600 mt-1 line-clamp-2 leading-snug">
                  {{ item.body }}
                </div>
              </VaListItemSection>
            </VaListItem>

            <VaListSeparator v-if="index !== formattedNotifications.length - 1" class="mx-3" />
          </template>
        </VaList>

        <div class="p-2 border-t">
          <VaButton preset="primary" class="w-full text-sm" size="small" @click="NotificationsPage">
            Lihat Semua Riwayat
          </VaButton>
        </div>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import { useRouter } from 'vue-router'
const router = useRouter()
import { useToast } from 'vuestic-ui' // Opsional: untuk feedback
import API from '../../../../services/api'

const isDropdownOpen = ref(false)
// Config
const POLL_INTERVAL = 30000 // 30 Detik
const { init: initToast } = useToast()

// State
const notifications = ref<any[]>([])
const unreadCount = ref(0)
const isLoading = ref(false)
let pollingTimer: any = null

// --- HELPER: Icon Mapping ---
const getIconByType = (type: string) => {
  switch (type) {
    case 'document_verification':
      return 'description'
    case 'payment':
      return 'payments'
    case 'booking':
      return 'book_online'
    case 'danger':
      return 'error'
    case 'success':
      return 'check_circle'
    default:
      return 'notifications'
  }
}

const NotificationsPage = () => {
  // Tutup dropdown (opsional, biasanya otomatis tertutup saat pindah route)
  // Arahkan ke route admin notifications
  isDropdownOpen.value = false
  router.push({ name: 'notifications' })
}

// --- API: Fetch Data ---
const fetchData = async () => {
  try {
    // 1. Ambil List Notifikasi
    const listReq = API.get('notifications', {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}` },
    })
    // 2. Ambil Jumlah Unread (Badge)
    const countReq = API.get('notifications/unread-count', {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}` },
    })

    const [listRes, countRes] = await Promise.all([listReq, countReq])

    // Sesuaikan dengan struktur JSON Laravel Anda (misal: data.data.data utk pagination)
    notifications.value = listRes.data.data.data
    unreadCount.value = countRes.data.data.unread_count
  } catch (error) {
    console.error('Gagal memuat notifikasi:', error)
  } finally {
    isLoading.value = false
  }
}

// --- ACTION: Mark ONE as Read ---
const handleItemClick = async (item: any) => {
  // Logic navigasi berdasarkan type/data
  if (item.data && item.data.link) {
    // router.push(item.data.link)
  }

  // Jika sudah dibaca, stop
  if (item.is_read_by_me) return

  // Optimistic UI Update (Ubah tampilan jadi 'read' dulu biar cepat)
  const target = notifications.value.find((n) => n.id === item.id)
  if (target) {
    target.is_read_by_me = true
    unreadCount.value = Math.max(0, unreadCount.value - 1)
  }

  // Kirim request ke backend (background process)
  try {
    await API.post(
      `notifications/${item.id}/read`,
      {},
      {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}` },
      },
    )
  } catch (e) {
    console.error(e)
  }
}

// --- ACTION: Mark ALL as Read ---
const handleMarkAllRead = async () => {
  if (unreadCount.value === 0) return

  // 1. Optimistic Update (Langsung nol-kan badge)
  unreadCount.value = 0
  notifications.value.forEach((n) => (n.is_read_by_me = true))

  // 2. Panggil API Backend
  try {
    await API.post(
      'notifications/read-all',
      {},
      {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}` },
      },
    )
    initToast({ message: 'Semua notifikasi ditandai sudah dibaca', color: 'success' })
  } catch (e) {
    console.error(e)
    // Revert jika gagal (opsional)
    fetchData()
  }
}

// --- COMPUTED: Formatting Data ---
const formattedNotifications = computed(() => {
  return notifications.value.map((item) => ({
    id: item.id,
    title: item.title,
    body: item.body,
    icon: getIconByType(item.type),
    // Prioritaskan 'is_read_by_me' (dari Accessor Laravel), fallback ke 'is_read'
    is_read: item.is_read_by_me ?? item.is_read,
    // Menggunakan created_at_human dari backend Laravel
    timeHuman: item.created_at_human || item.created_at,
    data: item.data,
  }))
})

// --- LIFECYCLE ---
onMounted(() => {
  isLoading.value = true
  fetchData()
  // Jalankan polling
  pollingTimer = setInterval(fetchData, POLL_INTERVAL)
})

onBeforeUnmount(() => {
  if (pollingTimer) clearInterval(pollingTimer)
})
</script>

<style scoped>
/* Styling tambahan jika perlu */
.notification-dropdown__icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
